/** Thin React shell that dynamically loads the internal SDK bundle and registers this instance via `registerCaller`. */

import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { loadInternalModule } from '../../loader.js';
import type { ApplicationContext, ClientTool } from '../../protocolTypes.js';
import type { CallerHandle, SdkMountOptions, SdkPlacement } from '../../sdkContract.js';

export interface OgAssistProps {
  /**
   * @deprecated Since 2.2.0. Use {@link OgAssistProps.getAccessToken} instead. A snapshot token
   * cannot refresh itself, so a long-lived mount eventually sends an expired one and requests fail
   * with `jwt.verify failed: jwt expired`. Remove once all embedders pass `getAccessToken`.
   */
  authBearerToken: string;

  /**
   * Resolves a currently-valid bearer token. Called before every request, so an expiring session
   * never reaches the wire. Pass `getAccessTokenSilently` from `@auth0/auth0-react` directly — it
   * refreshes transparently and is already reference-stable.
   *
   * Takes precedence over {@link OgAssistProps.authBearerToken} when both are supplied.
   *
   * @example
   * ```tsx
   * const { token, getAccessTokenSilently } = useAuth();
   *
   * // `authBearerToken` is still required while deprecated; `getAccessToken` is what gets used.
   * <OgAssist
   *   authBearerToken={token}
   *   getAccessToken={getAccessTokenSilently}
   *   entityID={entityId}
   *   baseURL={baseURL}
   * />
   * ```
   */
  getAccessToken?: () => Promise<string>;

  /** Entity ID (UUID) for the current context. */
  entityID: string;

  /** Base URL for API requests and module loading. */
  baseURL: string;

  /** Application-specific context passed to the agent. */
  applicationContext?: ApplicationContext;

  /** Custom tools that the agent can invoke in the client. */
  clientTools?: ClientTool[];

  /**
   * Overrides the heading shown in the welcome message. When omitted, the SDK's personalized default
   * (`Hello {first name}! Where should we begin?`) is used; pass an empty string to render no heading.
   */
  welcomeMessage?: string;

  /**
   * Overrides the suggestion prompts shown in the welcome message. When omitted, the SDK's defaults are
   * used; pass an empty array to render no suggestions.
   */
  welcomeSuggestions?: string[];

  /** Callback invoked when the chat drawer opens/closes. */
  onOpenChange?: (isOpen: boolean) => void;

  /** Whether the chat drawer should be open by default. */
  defaultOpen?: boolean;

  /**
   * Where the chat renders. Defaults to `'drawer'`.
   *
   * `'page'` fills the container: no launcher button, no close control, always open. `defaultOpen` is ignored,
   * `onOpenChange` never fires, and the imperative `open()` / `close()` no-op. `portalTargetRef` still applies — it
   * is where the page renders. Without one the chat fills this component's own element, so give its parent a
   * definite height.
   */
  placement?: SdkPlacement;

  /** DOM element to render the chat drawer portal into. */
  portalTargetRef?: React.RefObject<HTMLElement>;

  /** Whether the entity is still loading. */
  loading?: boolean;

  /** Whether there was an error loading entity data. */
  error?: boolean;

  /** Error message to display when error is true. */
  errorMessage?: string;

  /** When true, this instance's container takes precedence regardless of registration order. */
  primary?: boolean;
}

/**
 * Imperative handle exposed via `ref` on `<OgAssist>`. `null` until the internal bundle has loaded; consumers must
 * null-check before invoking methods. The handle does not buffer commands.
 */
export interface OgAssistHandle {
  /** Open the chat drawer. Idempotent. */
  open: () => void;
  /** Close the chat drawer. Idempotent. */
  close: () => void;
  /** Start a new conversation, optionally seeded with `opts.initialPrompt` and `opts.applicationContext`. */
  startNewConversation: (opts?: { initialPrompt?: string; applicationContext?: ApplicationContext }) => void;
}

/** Build `SdkMountOptions` from component props. */
function buildOptions(props: Omit<OgAssistProps, 'primary'>): Partial<SdkMountOptions> {
  return {
    authBearerToken: props.authBearerToken,
    getAccessToken: props.getAccessToken,
    entityID: props.entityID,
    baseURL: props.baseURL,
    applicationContext: props.applicationContext,
    clientTools: props.clientTools,
    welcomeMessage: props.welcomeMessage,
    welcomeSuggestions: props.welcomeSuggestions,
    onOpenChange: props.onOpenChange,
    defaultOpen: props.defaultOpen,
    placement: props.placement,
    portalTarget: props.portalTargetRef?.current ?? undefined,
    loading: props.loading,
    error: props.error,
    errorMessage: props.errorMessage
  };
}

export const OgAssist = forwardRef<OgAssistHandle, OgAssistProps>(function OgAssist(
  {
    authBearerToken,
    getAccessToken,
    entityID,
    baseURL,
    applicationContext,
    clientTools,
    welcomeMessage,
    welcomeSuggestions,
    onOpenChange,
    defaultOpen,
    placement,
    portalTargetRef,
    loading,
    error,
    errorMessage,
    primary = false
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Kept in React state (not a ref) so the update effect re-runs on the null→handle transition.
  const [callerHandle, setCallerHandle] = useState<CallerHandle | null>(null);

  /**
   * Whether a credential is present at all. Registration keys on this rather than on the token's
   * value: a rotated token is the same session and must not tear down the registration, but
   * acquiring or losing one (sign-in / sign-out) is a genuine lifecycle change.
   */
  const hasCredential = Boolean(authBearerToken) || getAccessToken !== undefined;

  // Register on mount; re-register when an identity-affecting prop changes. The token's *value* is
  // not identity — rotations are pushed through `callerHandle.update()` by the effect below, which
  // re-renders the existing root instead of unmounting it, so the open conversation survives.
  useEffect(() => {
    if (!hasCredential || !containerRef.current) return;

    const abortController = new AbortController();
    const signal = abortController.signal;
    let handle: CallerHandle | null = null;

    loadInternalModule(baseURL)
      .then((internal) => {
        if (signal.aborted || !containerRef.current) return;

        if (typeof internal.registerCaller !== 'function') {
          console.error(
            '[OgAssist] Internal bundle is missing `registerCaller`. ' +
              'Deploy a newer @opengov/agents-browser-sdk-internal at this baseURL. ' +
              'OgAssist will not render.'
          );
          return;
        }

        const options = buildOptions({
          authBearerToken,
          getAccessToken,
          entityID,
          baseURL,
          applicationContext,
          clientTools,
          welcomeMessage,
          welcomeSuggestions,
          onOpenChange,
          defaultOpen,
          placement,
          portalTargetRef,
          loading,
          error,
          errorMessage
        });

        handle = internal.registerCaller(containerRef.current, options, primary);
        setCallerHandle(handle);
      })
      .catch((loadError: unknown) => {
        if (signal.aborted) return;
        console.error('Failed to load internal SDK module:', loadError);
      });

    return () => {
      abortController.abort();
      if (handle !== null) {
        handle.deregister();
        handle = null;
      }
      setCallerHandle(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCredential, entityID, baseURL, primary]);

  // Push contribution changes through the handle. `callerHandle` is in deps so the effect fires on the
  // null→handle transition and flushes any props that changed during the in-flight load.
  useEffect(() => {
    if (callerHandle === null) return;

    const options = buildOptions({
      authBearerToken,
      getAccessToken,
      entityID,
      baseURL,
      applicationContext,
      clientTools,
      welcomeMessage,
      welcomeSuggestions,
      onOpenChange,
      defaultOpen,
      placement,
      portalTargetRef,
      loading,
      error,
      errorMessage
    });

    callerHandle.update(options, primary);
  }, [
    callerHandle,
    applicationContext,
    clientTools,
    welcomeMessage,
    welcomeSuggestions,
    onOpenChange,
    defaultOpen,
    placement,
    portalTargetRef,
    authBearerToken,
    getAccessToken,
    entityID,
    baseURL,
    loading,
    error,
    errorMessage,
    primary
  ]);

  // Consumer-facing imperative handle. Null until registration succeeds.
  const handle = useMemo<OgAssistHandle | null>(() => {
    if (callerHandle === null) {
      return null;
    }
    return {
      open: () => {
        callerHandle.openChat();
      },
      close: () => {
        callerHandle.closeChat();
      },
      startNewConversation: (opts) => {
        callerHandle.startNewConversation(opts);
      }
    };
  }, [callerHandle]);

  // Forward the handle to the consumer-provided ref. Direct assignment (not useImperativeHandle) so the ref is
  // genuinely null until ready.
  useEffect(() => {
    if (ref === null) return;
    if (typeof ref === 'function') {
      ref(handle);
      return () => {
        ref(null);
      };
    }
    ref.current = handle;
    return () => {
      ref.current = null;
    };
  }, [handle, ref]);

  return <div ref={containerRef} style={placement === 'page' ? { height: '100%' } : undefined} />;
});
